// mountains/rocks.ts - Rock scenery: meshes, snow-capped colouring, placement,
// and the collision-hazard subset.
//
// THREE-heavy (browser-only) plus the placement-time collision rules. Rocks are
// scenery — not on any physics/determinism path — but a placed rock large enough
// and clear of the ski corridor/spawn pocket is reported as a collision hazard
// (radius capped at 3u, in sync with snowman.ts's inline rock collision). Reads the
// analytic terrain samplers from terrain.js. Extracted from the mountains hub
// (Stage R-mountains, issue #34).
import * as THREE from 'three';
import { batchStaticRocks } from './rock-batches.js';
import {
  getTerrainHeight, getTerrainGradient,
  // Cache-neutral samplers for the RENDER-ONLY grounding layer (#385 PR 4): the
  // cached samplers memoize every ad-hoc query into the shared heightMap that
  // later tree placement and live physics read, so the collar/chip vertex
  // sampling must bypass the cache or grounding would perturb downstream
  // terrain samples (Codex review on #390).
  getTerrainHeightUncached, getTerrainGradientUncached,
} from './terrain.js';
// Guards the shared material's THREE UUID draw so it cannot perturb the caller's seeded
// global Math.random stream (same guard trees.ts uses for its pooled materials).
import { withPrivateThreeRandom } from '../scenery/scenery-rng.js';
// The run's centerline: the clear collidable corridor + cliff exclusion follow it, and
// a Black run adds deterministic rock-gate pinches along it. activeLaneX is exactly 0
// for straight tiers, so `x - lane` collapses to `x` and Bunny/Blue stay byte-identical.
import { activeLaneX, getActiveCourseLine } from '../course-line.js';
import { SNOW_WHITE } from './snow-palette.js';

/** A rendered rock's world position and size — the render-only bookkeeping shape
 *  (contact shadows read the full rendered set via addRocks' outAllRendered). */
export interface RenderedRockPosition {
  x: number;
  y: number;
  z: number;
  size: number;
}

/** A COLLIDABLE rock hazard (#348, #385 PR 5): extends the rendered shape with the
 *  world-space top of the actual placed mesh, so the in-game jump-clearance check
 *  can track what the player sees instead of the old flat `y + 0.7·size` model.
 *  `topY` is computed from the post-rotation bounding box over the CANONICAL sink
 *  depth (see hazardTopY) and is REQUIRED on every hazard addRocks returns. */
export interface RockHazardPosition extends RenderedRockPosition {
  topY: number;
}

/** Back-compat alias (the pre-#348 name most callers import). */
export type RockPosition = RenderedRockPosition;

export const ROCK_COLLISION_MIN_SIZE = 1.25;

// Keep the central ski line and the spawn pocket clear of *collidable* rocks: the
// run must always stay navigable (mirrors the tree clear-corridor in trees.ts) and
// the player must never spawn on, or right next to, a hazard. Decorative rocks are
// still rendered everywhere — only their hazard status is suppressed here. Both
// exclusions are widened by the rock's own collision radius below, so a rock whose
// *center* sits just outside the corridor/pocket can't still reach into it.
const ROCK_COLLISION_PATH_HALF_WIDTH = 5;     // central ski-line corridor half-width, before the rock radius is added
const ROCK_COLLISION_START_CLEAR_RADIUS = 10; // clear pocket around the snowman start, before the rock radius is added
// Snowman spawn, mirroring resetSnowman() in snowman.ts (pos.x = 0, pos.z = -15).
const SNOWMAN_START_X = 0;
const SNOWMAN_START_Z = -15;

/**
 * Collision radius (world units) of a collidable rock of the given size (max 3u).
 * The placement-time safe-zone below uses this to exclude rocks whose hazard radius
 * would reach the ski lane or spawn pocket. snowman.ts's in-game rock collision uses
 * the identical formula inline (it must stay free of relative imports so the
 * no-resolve-hook Node test harnesses can load it) — keep the two in sync.
 */
export function rockCollisionRadius(size: number): number {
  return Math.max(1.25, Math.min(3.0, size * 0.75 + 0.75));
}

/**
 * Whether a placed rock should act as a collision hazard. A rock qualifies only
 * when it is large enough to read as an obstacle AND clear of both the central ski
 * line and the spawn pocket — each exclusion expanded by the rock's own collision
 * radius so the unseeded random placement can never reach into the ski lane, wall
 * off the run, or crash the player on spawn.
 */
export function rockIsCollisionHazard(x: number, z: number, size: number): boolean {
  if (size < ROCK_COLLISION_MIN_SIZE) return false;
  const radius = rockCollisionRadius(size);
  // Clearance is measured from the run's centerline at this z (the winding corridor for
  // Black); lane === 0 for straight tiers, so this stays `Math.abs(x)` for Bunny/Blue.
  if (Math.abs(x - activeLaneX(z)) < ROCK_COLLISION_PATH_HALF_WIDTH + radius) return false;
  const dx = x - SNOWMAN_START_X;
  const dz = z - SNOWMAN_START_Z;
  if (Math.sqrt(dx * dx + dz * dz) < ROCK_COLLISION_START_CLEAR_RADIUS + radius) return false;
  return true;
}

// Shared craggy normal map for all rocks (issue #17, Stage 2). Built once and
// reused across the few hundred rock meshes — cheap, and avoids per-rock canvas
// work. Lazily created so it's only touched in a DOM/browser context (createRock
// is browser-only via createTerrain/addRocks); returns null elsewhere so the
// material simply renders without it. Same legacy-pipeline / NoColorSpace
// handling as the snow normal map above.
let rockNormalTexture: THREE.CanvasTexture | null = null;
function getRockNormalTexture(): THREE.CanvasTexture | null {
  if (rockNormalTexture) return rockNormalTexture;
  if (typeof document === 'undefined') return null;
  const SIZE = 128;
  const height = new Float32Array(SIZE * SIZE);
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE, v = y / SIZE;
      // Chaotic high-frequency relief for a rough rock face (tileable).
      let h = 0.5 * Math.sin(2 * Math.PI * (5 * u + 3 * v) + 0.4);
      h += 0.4 * Math.sin(2 * Math.PI * (9 * u - 7 * v) + 1.1);
      h += 0.3 * Math.sin(2 * Math.PI * (17 * u + 11 * v) + 2.3);
      h += 0.2 * Math.sin(2 * Math.PI * 23 * u) * Math.sin(2 * Math.PI * 29 * v);
      height[y * SIZE + x] = h;
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  const image = ctx.createImageData(SIZE, SIZE);
  const data = image.data;
  const STRENGTH = 2.5;
  const wrap = (i: number) => (i + SIZE) % SIZE;
  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const hl = height[y * SIZE + wrap(x - 1)]!;
      const hr = height[y * SIZE + wrap(x + 1)]!;
      const hd = height[wrap(y - 1) * SIZE + x]!;
      const hu = height[wrap(y + 1) * SIZE + x]!;
      let nx = -(hr - hl) * STRENGTH;
      let ny = -(hu - hd) * STRENGTH;
      const nz = 1.0;
      const len = Math.hypot(nx, ny, nz);
      nx /= len; ny /= len;
      const idx = (y * SIZE + x) * 4;
      data[idx] = (nx * 0.5 + 0.5) * 255;
      data[idx + 1] = (ny * 0.5 + 0.5) * 255;
      data[idx + 2] = (nz / len * 0.5 + 0.5) * 255;
      data[idx + 3] = 255;
    }
  }
  ctx.putImageData(image, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  rockNormalTexture = tex;
  return tex;
}

// Number of global Math.random() values THREE draws for one Material's UUID. main built
// one MeshStandardMaterial per rock (that draw happened per rock); with the shared
// materials below it happens once, so createRock burns this many draws per rock to keep
// the per-rock global budget — and thus downstream placement — byte-identical to main.
const ROCK_MATERIAL_UUID_DRAWS = 4;

// Two shared rock materials (boulder / cliff), built once and reused across the few
// hundred rock meshes instead of one MeshStandardMaterial per rock. Safe because the
// per-rock stone hue + snow cap ride in each geometry's vertex-colour attribute (see
// applyRockSnowColors); only roughness and normalScale differ by rock type. Lazily
// created and cached; nulled by resetRockCaches on teardown.
let boulderMaterial: THREE.MeshStandardMaterial | null = null;
let cliffMaterial: THREE.MeshStandardMaterial | null = null;
function getRockMaterial(cliff: boolean): THREE.MeshStandardMaterial {
  const existing = cliff ? cliffMaterial : boulderMaterial;
  if (existing) return existing;
  // Resolve the shared normal map OUTSIDE the private-RNG guard so its one-time texture
  // UUID draw still lands on the global stream exactly as main's did (main created it on
  // the first rock). Only the material's own UUID draw is guarded, and it is compensated
  // per rock by the burn in createRock.
  const normalMap = getRockNormalTexture();
  const mat = withPrivateThreeRandom(() => new THREE.MeshStandardMaterial({
    color: 0xffffff,
    roughness: cliff ? 0.95 : 0.9,
    metalness: 0.0, // matte rock/snow, not the shiny grey crystal it read as before
    flatShading: true,
    vertexColors: true,
    normalMap,
    normalScale: new THREE.Vector2(cliff ? 0.6 : 0.4, cliff ? 0.6 : 0.4)
  }));
  if (cliff) cliffMaterial = mat; else boulderMaterial = mat;
  return mat;
}

/**
 * Null (and defensively dispose) the module-level rock singletons so a later rebuild
 * re-creates them cleanly instead of holding freed-but-still-referenced GPU handles.
 * Mirrors resetTreePools: disposeSceneResources already disposes the scene-attached
 * copies, and dispose() is idempotent, so this is safe to call alongside it on teardown.
 * Also frees the shared rock normal texture, which previously had no reset (a latent
 * cross-session leak: the sweep disposed it but the module cache kept the dead handle).
 */
export function resetRockCaches(): void {
  const free = (r: { dispose?: () => void } | null): void => {
    if (r && typeof r.dispose === 'function') r.dispose();
  };
  free(boulderMaterial);
  free(cliffMaterial);
  free(collarMaterial);
  free(rockNormalTexture);
  boulderMaterial = null;
  cliffMaterial = null;
  collarMaterial = null;
  rockNormalTexture = null;
}

// Shared snow-collar material (rock grounding, #385 PR 4): ONE colour-only
// MeshStandardMaterial for the merged collar mesh. Plain (no vertexColors, no
// normalMap, smooth shading), so it reuses an already-compiled standard program
// (+0 programs — same reasoning as the snowman face materials). Lazily created and
// cached; nulled by resetRockCaches on teardown. The pebble chips need no material
// of their own: their merged geometry bakes uv + vertex colours and reuses the
// shared boulder material (+0 materials, +0 programs).
let collarMaterial: THREE.MeshStandardMaterial | null = null;
function getCollarMaterial(): THREE.MeshStandardMaterial {
  if (collarMaterial) return collarMaterial;
  collarMaterial = withPrivateThreeRandom(() => new THREE.MeshStandardMaterial({
    // A whisper below the terrain's snow white so the drift reads as a soft mound,
    // not a glowing decal (tinted from the shared snow palette's SNOW_WHITE).
    color: new THREE.Color(SNOW_WHITE.r * 0.985, SNOW_WHITE.g * 0.99, SNOW_WHITE.b),
    roughness: 0.96,
    metalness: 0.0,
  }));
  return collarMaterial;
}

// --- Grounding: slope-aware sink, snow collars, pebble chips (#385 PR 4) ------------
// Render-only integration: rocks must sit IN the snow, not pasted onto it. Nothing
// here touches rockPositions, outAllRendered, collision, or the global Math.random
// stream (all variation is private-seeded; every THREE construction is wrapped in
// withPrivateThreeRandom so UUID draws cannot shift the downstream tree placement —
// addRocks runs BEFORE Trees.addTrees on the seeded global stream).

/**
 * How deep a placed rock's mesh sinks below the terrain sample (world units).
 * Boulders sink slightly deeper on steep pitches (drifted-in look); cliffs keep
 * their fixed 0.28·size seat — their tops are the #348 collision story and must
 * not move until PR 5. Pure + NaN-safe (a non-finite steepness gets the flat
 * sink); exported for the grounding tests. Deeper sinking is collision-conservative
 * (the frozen size-based check registers hits above the visible top).
 */
export function rockSinkDepth(size: number, steepness: number, cliff: boolean): number {
  if (cliff) return size * 0.28;
  const extra = steepness > 0 ? Math.min(0.08, steepness * 0.12) : 0;
  return size * (0.3 + extra);
}

/** Grounding entry recorded during placement (private-seeded decoration inputs). */
interface GroundingEntry { x: number; z: number; size: number; seed: number; }

const COLLAR_MIN_SIZE = 1.6;     // collars only around rocks big enough to drift against
const CHIP_MIN_CLIFF_SIZE = 3.2; // pebble chips only near genuinely large cliff blocks

/**
 * Uphill-biased collar centre for a rock at (x, z): snow drifts pile against the
 * UPHILL face of an obstacle, so the mound centre shifts along +gradient. Pure;
 * exported for the grounding tests (the #317 anti-pattern regression suite).
 */
export function collarCenterFor(x: number, z: number, size: number): { x: number; z: number } {
  const g = getTerrainGradientUncached(x, z);
  const len = Math.hypot(g.x, g.z);
  if (!(len > 1e-6)) return { x, z };
  return { x: x + (g.x / len) * 0.25 * size, z: z + (g.z / len) * 0.25 * size };
}

/**
 * Build ONE merged, indexed dome-field geometry for every snow collar. Each collar
 * is DRAPED onto the terrain: every vertex's y is `getTerrainHeight(x, z)` plus a
 * radial mound profile (minus a small tuck-under sink) — terrain-conforming by
 * construction, so a collar can never float off a slope or mogul (the #317
 * floating-disc anti-pattern is impossible by shape, not merely discouraged). The
 * rim radius is elongated uphill and noised per spoke so the edge reads as a
 * wind-drifted irregular mound, not a stamped disc.
 */
function buildCollarGeometry(entries: GroundingEntry[]): THREE.BufferGeometry {
  const RINGS = 4, SEGS = 12;
  const vertsPer = (RINGS + 1) * SEGS;
  const positions = new Float32Array(entries.length * vertsPer * 3);
  const indices: number[] = [];
  let vBase = 0, p = 0;
  for (const e of entries) {
    const rng = mulberry32((e.seed ^ 0x0C011A9) >>> 0);
    const R0 = e.size * (1.25 + rng() * 0.35);
    const H = e.size * (0.15 + rng() * 0.08);
    const sink = 0.05 * e.size;
    const c = collarCenterFor(e.x, e.z, e.size);
    const g = getTerrainGradientUncached(e.x, e.z);
    const gLen = Math.hypot(g.x, g.z);
    const ux = gLen > 1e-6 ? g.x / gLen : 0;
    const uz = gLen > 1e-6 ? g.z / gLen : 0;
    // Per-spoke rim noise, fixed per collar so all rings share the same outline.
    const spokeNoise: number[] = [];
    for (let j = 0; j < SEGS; j++) spokeNoise.push(0.85 + rng() * 0.3);
    for (let i = 0; i <= RINGS; i++) {
      const t = i / RINGS;
      const profile = H * (1 - t * t) ** 2;
      for (let j = 0; j < SEGS; j++) {
        const th = (j / SEGS) * Math.PI * 2;
        const dx = Math.cos(th), dz = Math.sin(th);
        // Rim stretched toward uphill (dot(dir, uphill)) + per-spoke noise.
        const R = R0 * (1 + 0.22 * (dx * ux + dz * uz)) * spokeNoise[j]!;
        const x = c.x + dx * R * t;
        const z = c.z + dz * R * t;
        positions[p++] = x;
        positions[p++] = getTerrainHeightUncached(x, z) + profile - sink;
        positions[p++] = z;
      }
    }
    for (let i = 0; i < RINGS; i++) {
      for (let j = 0; j < SEGS; j++) {
        const j2 = (j + 1) % SEGS;
        const a = vBase + i * SEGS + j;
        const b = vBase + i * SEGS + j2;
        const cIdx = vBase + (i + 1) * SEGS + j;
        const d = vBase + (i + 1) * SEGS + j2;
        indices.push(a, cIdx, b, b, cIdx, d);
      }
    }
    vBase += vertsPer;
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Build ONE merged pebble-chip geometry: a few tiny tumbled dodecahedra scattered
 * around each large cliff block (rockfall debris at the foot of a crag). Mostly
 * buried, never collidable, never in rockPositions/outAllRendered. Bakes uv +
 * per-chip stone vertex colours so the mesh reuses the shared boulder material.
 */
function buildChipGeometry(anchors: GroundingEntry[]): THREE.BufferGeometry {
  const template = new THREE.DodecahedronGeometry(1, 0);
  const tPos = template.attributes.position!.array as Float32Array;
  const tUv = template.attributes.uv!.array as Float32Array;
  const vertsPer = template.attributes.position!.count;
  const chunks: Array<{ px: Float32Array; col: Float32Array }> = [];
  const m = new THREE.Matrix4();
  const eu = new THREE.Euler();
  const v = new THREE.Vector3();
  const color = new THREE.Color();
  for (const a of anchors) {
    const rng = mulberry32((a.seed ^ 0x9D2C562) >>> 0);
    const n = 4 + Math.floor(rng() * 4);
    for (let k = 0; k < n; k++) {
      const s = Math.min(0.5, a.size * (0.06 + rng() * 0.05));
      const th = rng() * Math.PI * 2;
      const dist = a.size * (1.15 + rng() * 0.8);
      const cx = a.x + Math.cos(th) * dist;
      const cz = a.z + Math.sin(th) * dist;
      const cy = getTerrainHeightUncached(cx, cz) + s * 0.15; // mostly buried pebble
      eu.set((rng() - 0.5) * 0.9, rng() * Math.PI * 2, (rng() - 0.5) * 0.9);
      m.makeRotationFromEuler(eu);
      // Chip colour: a stone tone from the shared palette via the private stream
      // (the global makeRockColor path is never touched).
      const pick = ROCK_STONES[Math.floor(rng() * ROCK_STONES.length)]!;
      color.setHSL(pick.h, pick.s, Math.max(0.14, pick.l - 0.04));
      const px = new Float32Array(vertsPer * 3);
      const col = new Float32Array(vertsPer * 3);
      for (let i = 0; i < vertsPer; i++) {
        v.set(tPos[i * 3]!, tPos[i * 3 + 1]!, tPos[i * 3 + 2]!)
          .applyMatrix4(m).multiplyScalar(s);
        px[i * 3] = v.x + cx;
        px[i * 3 + 1] = v.y + cy;
        px[i * 3 + 2] = v.z + cz;
        col[i * 3] = color.r; col[i * 3 + 1] = color.g; col[i * 3 + 2] = color.b;
      }
      chunks.push({ px, col });
    }
  }
  template.dispose();
  const total = chunks.length * vertsPer;
  const positions = new Float32Array(total * 3);
  const colors = new Float32Array(total * 3);
  const uvs = new Float32Array(total * 2);
  chunks.forEach((chunk, i) => {
    positions.set(chunk.px, i * vertsPer * 3);
    colors.set(chunk.col, i * vertsPer * 3);
    uvs.set(tUv, i * vertsPer * 2);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.computeVertexNormals();
  return geometry;
}

/**
 * Attach the grounding decoration meshes (one merged collar mesh + one merged chip
 * mesh — two draw calls total, zero new shader programs). Tagged for the re-run
 * de-dup sweep in addRocks.
 */
function addRockGrounding(
  scene: THREE.Scene, collars: GroundingEntry[], chips: GroundingEntry[]
): void {
  withPrivateThreeRandom(() => {
    if (collars.length > 0) {
      const mesh = new THREE.Mesh(buildCollarGeometry(collars), getCollarMaterial());
      mesh.receiveShadow = true;
      mesh.userData.isRockGrounding = true;
      mesh.userData.isRockSnowCollar = true;
      scene.add(mesh);
    }
    if (chips.length > 0) {
      const mesh = new THREE.Mesh(buildChipGeometry(chips), getRockMaterial(false));
      mesh.receiveShadow = true;
      mesh.userData.isRockGrounding = true;
      mesh.userData.isRockChips = true;
      scene.add(mesh);
    }
  });
}

// --- Face-aware snow pass (rock realism recovery PR 2, issue #385) ------------------
// smoothstep helper (kept local: no THREE.MathUtils dependency on this hot path).
function smoothstep(lo: number, hi: number, v: number): number {
  const t = Math.min(1, Math.max(0, (v - lo) / (hi - lo)));
  return t * t * (3 - 2 * t);
}

/** NaN-safe clamp to [0,1] (NaN fails `v > 0` and returns 0). */
function clamp01(v: number): number {
  return v > 0 ? Math.min(v, 1) : 0;
}

/**
 * Deterministic per-face hash → [0,1). Keyed by the rock's shape seed and the face
 * index, so the same {seed, size, cliff} bakes byte-identical colours while adjacent
 * faces stay uncorrelated (the broken, natural snow edge). Zero global Math.random.
 */
function faceHash01(seed: number, face: number): number {
  let h = (seed ^ Math.imul(face + 1, 0x9E3779B9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 4294967296;
}

/**
 * Bake snow + shading into a rock's vertex colours, face by face.
 *
 * The old rule was a pure normal band (ny 0.05→0.55 smoothstep): *any* sufficiently
 * up-facing area saturated to full white — natural top shelf, broad cliff slab, and
 * low side facet alike. That coupling is what made both prior geometries read as
 * white ice (#344/#346): the coloring whitened whatever geometry was presented to it.
 *
 * The face-aware pass restrains snow to where it would actually gather:
 *  - FULL snow only on strongly up-facing shelves in the rock's upper half
 *    (shelf band × height bias), with a per-face seeded threshold jitter so the
 *    snow line breaks naturally instead of following one clean iso-band;
 *  - LIGHT dusting (≤ ~0.22 blend — still clearly stone) on moderate side slopes;
 *  - broad cliff slabs below the cap stay rock even when tilted upward;
 *  - undersides and low crevices darken ~8–20% so grounded rocks shade into their
 *    seat instead of glowing.
 *
 * Deterministic and RNG-stream-neutral: every per-face variation comes from
 * faceHash01(seed, face) — zero global Math.random() draws (the 448/340 per-rock
 * budget is pinned by rock-material-tests.js). Same {seed, size, cliff} ⇒
 * byte-identical colour buffer (pinned by rock-snow-color-tests.js).
 *
 * The deformed dodecahedron is non-indexed (3 verts per face), so colours are
 * written per face. Mutates `geometry` in place; call after computeVertexNormals().
 */
function applyRockSnowColors(
  geometry: THREE.BufferGeometry,
  rockColor: THREE.Color,
  opts: { cliff: boolean; seed: number }
): void {
  const positions = geometry.attributes.position!.array as Float32Array;
  const count = geometry.attributes.position!.count;
  const faceCount = count / 3;
  const colors = new Float32Array(count * 3);
  const snowCol = SNOW_WHITE;

  // Pass 1: face normals/areas/centroid heights + rock height range and mean face
  // area (the slab test below is relative to this rock's own tessellation).
  const faceNy = new Float32Array(faceCount);
  const faceArea = new Float32Array(faceCount);
  const faceCy = new Float32Array(faceCount);
  let minY = Infinity, maxY = -Infinity, areaSum = 0;
  for (let f = 0; f < faceCount; f++) {
    const i = f * 9;
    const ax = positions[i]!, ay = positions[i + 1]!, az = positions[i + 2]!;
    const bx = positions[i + 3]!, by = positions[i + 4]!, bz = positions[i + 5]!;
    const cx = positions[i + 6]!, cy = positions[i + 7]!, cz = positions[i + 8]!;
    const ux = bx - ax, uy = by - ay, uz = bz - az;
    const vx = cx - ax, vy = cy - ay, vz = cz - az;
    const nx = uy * vz - uz * vy;
    const nyc = uz * vx - ux * vz;
    const nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, nyc, nz);
    faceNy[f] = len > 0 ? nyc / len : 0;
    faceArea[f] = len / 2;
    areaSum += faceArea[f]!;
    faceCy[f] = (ay + by + cy) / 3;
    minY = Math.min(minY, ay, by, cy);
    maxY = Math.max(maxY, ay, by, cy);
  }
  const meanArea = areaSum / faceCount;
  const ySpan = maxY - minY || 1;

  // Pass 2: per-face snow amount + shading, written to all three face vertices.
  for (let f = 0; f < faceCount; f++) {
    const ny = faceNy[f]!;
    const hy = clamp01((faceCy[f]! - minY) / ySpan);
    const u = faceHash01(opts.seed, f);

    // Shelf band with a seeded, per-face broken edge: full snow needs a STRONGLY
    // up-facing face (a rounded boulder's whole upper hemisphere sits above the old
    // 0.55 threshold, which is exactly how rocks read as all-white domes from the
    // player's high camera) — the band centre jitters so the snow line is ragged.
    const shelf = smoothstep(0.66 + (u - 0.5) * 0.20, 0.88 + (u - 0.5) * 0.08, ny);
    // ...and it must sit high on the rock to hold a shelf at all.
    const heightBias = smoothstep(0.30, 0.70, hy);
    let snow = shelf * heightBias;
    // Wind-scoured breakup: about a quarter of would-be shelves stay mostly bare,
    // so the cap is broken snow patches over visible stone, not one white lid.
    if (u < 0.25) snow *= 0.25 + u;
    // Light dusting on moderate side slopes: visibly stone, never white.
    const dust = smoothstep(0.35, 0.65, ny) * 0.18 * heightBias;
    snow = Math.max(snow, dust);
    // Broad cliff slabs below the cap stay bare rock (the #344 whole-facet-white
    // failure): a face much larger than this rock's mean facet only keeps full
    // snow when it is genuinely top-facing.
    if (opts.cliff && ny < 0.78 && faceArea[f]! > 2.5 * meanArea) {
      snow = Math.min(snow, 0.30);
    }

    // Undersides and low crevices darken so the rock shades into its seat.
    const down = clamp01((0.05 - ny) / 0.65);
    const cavity = 1 - smoothstep(0.0, 0.35, hy);
    const shade = 1 - 0.13 * down - 0.07 * cavity;

    const r = rockColor.r * shade + (snowCol.r - rockColor.r * shade) * snow;
    const g = rockColor.g * shade + (snowCol.g - rockColor.g * shade) * snow;
    const b = rockColor.b * shade + (snowCol.b - rockColor.b * shade) * snow;
    for (let k = 0; k < 3; k++) {
      const ci = (f * 3 + k) * 3;
      colors[ci] = r;
      colors[ci + 1] = g;
      colors[ci + 2] = b;
    }
  }
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
}

// A small palette of realistic mountain-stone base tones (HSL). Snow accumulates on
// top via applyRockSnowColors, so these are the bare-rock hues seen on the faces and
// undersides: cool granite grey, dark slate/charcoal, warm brown/tan, an iron-stained
// reddish, and a faint olive lichen. All kept low-saturation — real stone is mostly
// desaturated — and weighted toward the greys/browns so the slope reads as natural
// rock, not a gem field. Authored as setHSL values for the project's legacy linear
// pipeline (ColorManagement disabled), matching the existing tree trunk/foliage
// colours. Replaces the old uniform grey so the scattered rocks read as varied stone.
interface RockStone { h: number; s: number; l: number; weight: number; }
// Lightness is authored for the scene's light rig, not for a neutral viewer: the
// physically-correct rig (ambient 0.26π + hemi 0.62π + key 0.5π) multiplies albedo by
// ~1.4 on lit faces, so the old mid-grey values (granite 0.46) rendered as near-white
// "foil" against the snow (#385 PR 2 gallery). These sit ~0.10 lower so lit stone
// lands in the photographed-alpine-granite mid-grey band and stays clearly darker
// than the SNOW_WHITE shelves.
const ROCK_STONES: RockStone[] = [
  { h: 0.60, s: 0.05, l: 0.36, weight: 4 }, // cool granite grey
  { h: 0.62, s: 0.09, l: 0.24, weight: 3 }, // dark slate / charcoal
  { h: 0.08, s: 0.20, l: 0.32, weight: 3 }, // warm brown / tan
  { h: 0.04, s: 0.30, l: 0.27, weight: 2 }, // iron-stained reddish
  { h: 0.22, s: 0.12, l: 0.34, weight: 1 }, // faint olive lichen
];
const ROCK_STONE_WEIGHT = ROCK_STONES.reduce((s, e) => s + e.weight, 0);

/**
 * Pick a per-rock base colour: a weighted-random stone tone jittered in hue,
 * saturation, and lightness so no two rocks read identical. `darken` (0..1) pushes a
 * cliff/outcrop toward a deeper, more dramatic stone.
 */
function makeRockColor(darken = 0): THREE.Color {
  let r = Math.random() * ROCK_STONE_WEIGHT;
  let stone = ROCK_STONES[0]!;
  for (const e of ROCK_STONES) { stone = e; r -= e.weight; if (r <= 0) break; }
  const h = stone.h + (Math.random() - 0.5) * 0.03;
  const s = Math.min(0.45, Math.max(0, stone.s + (Math.random() - 0.5) * 0.06));
  const l = Math.min(0.7, Math.max(0.14, stone.l + (Math.random() - 0.5) * 0.12 - darken * 0.13));
  return new THREE.Color().setHSL(h, s, l);
}

// --- Seeded scrape pass ------------------------------------------------------
// gl-rock-style plane scrapes give the dodecahedron flat facets and shear faces.
// RNG-stream-neutral: a private mulberry32 stream (seeded per rock) does every
// draw, so the global Math.random() sequence — which tier determinism and the
// downstream tree/rock placement order depend on — is completely untouched.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Fallback seed when the caller doesn't pass one: a stepped counter, not
// Math.random(), so createRock stays stream-neutral even seedless (mirrors
// ez-forest's private xorshift precedent for THREE uuid draws).
let rockSeedCounter = 0x2F6E2B1;

// Scrape geometry bounds. Vertices are never pulled inside KEEP_HULL_RADIUS of the
// rock's core: placement sinks boulders by size*0.3 and cliffs by size*0.28, so a
// hull floor at 0.45*size keeps every rock grounded with no floating or open base.
const SCRAPE_KEEP_HULL_RADIUS = 0.45;
const SCRAPE_OFFSET_MIN = 0.55; // plane offset r ∈ [0.55, 0.9]·size
const SCRAPE_OFFSET_SPAN = 0.35;

/** Options for createRock. `cliff` builds a larger, more angular, darker outcrop. */
export interface RockOptions {
  cliff?: boolean;
  /** Deterministic scrape-shape seed; same seed + size + cliff ⇒ identical scrape
   *  planes (the pre-existing radial jitter still rides the global Math.random()). */
  seed?: number;
  /** Disable the plane-scrape pass (default on). */
  scrape?: boolean;
  /** Override the number of scrape planes (default: boulders 3–5, cliffs 6–9). */
  scrapeCount?: number;
  /** 0..1 plane hardness — how fully vertices flatten onto each plane (default 1). */
  scrapeStrength?: number;
  /** Disable the mass-preserving silhouette warp (default on; #385 PR 3). */
  warp?: boolean;
  /** Hard cap (world units) on the FINAL horizontal vertex reach, applied after
   *  every shaping pass. Placement contract for rocks with a guaranteed clear
   *  margin (the corridor pinch gates: PINCH_EDGE − corridor half-width = 3u) —
   *  the base jitter alone can reach 1.375·size, so without this a gate crag's
   *  widest rim can visibly poke into the advertised safe path (#389 review). */
  maxHorizontalReach?: number;
}

// --- Mass-preserving silhouette warp (rock realism recovery PR 3, issue #385) --------
// The #344 postmortem's structural lesson: silhouettes must improve WITHOUT losing the
// dodecahedron's mass. So this pass never replaces the solid — it deforms it under
// hard conservation rules, applied between the (untouched, global-stream) vertex
// jitter and the seeded scrape pass, so scrape facets stay flat planes cut into the
// warped mass.
//
// Hard rules, enforced in-code (and pinned by rocks-shape-tests + the metrics gate):
//  - MEAN RADIUS PRESERVED exactly (a final uniform renormalisation) — no shrinkage.
//  - HORIZONTAL GUARD: the max horizontal vertex radius may grow ≤5% over the
//    pre-warp shape. Collision stays the frozen size-based radius until #348 lands
//    (PR 5), so outward lobes must not push visible mass past what that check
//    covers — tightest on the Black pinch gates.
//  - RADIAL ENVELOPES unchanged: boulders stay inside 1.25·size, cliffs 1.8·size
//    (the grounding envelopes placement's sink depths were tuned against).
//  - Private seeded stream only — zero global Math.random() draws (the 448/340
//    budget and downstream tree placement are untouched).
const WARP_MAX_RADIAL_BOULDER = 1.25;
const WARP_MAX_RADIAL_CLIFF = 1.8;
const WARP_MAX_HORIZONTAL_GROWTH = 1.05;

/**
 * Deform the jittered solid into a bulkier, less spherical mass:
 *  - a volume-neutral ellipsoid scale (sx·sy·sz = 1) — ovalness without mass change;
 *    cliffs keep y nearly fixed (their jitter already stretches them tall, and the
 *    visual top must stay inside the pre-#348 collision envelope);
 *  - a mild shear (rock strata lean; cliffs lean harder);
 *  - 2–3 OUTWARD-biased lobes (never inward cuts — that direction is the hull
 *    failure, #346): each pushes vertices facing its direction out by up to
 *    ~0.14·size, horizontal-biased on cliffs so they read as shoulders/buttresses.
 */
function massWarpRock(
  positions: Float32Array,
  size: number,
  opts: { cliff: boolean; seed: number }
): number {
  const cliff = opts.cliff;
  const rng = mulberry32((opts.seed ^ 0x517CC1B7) >>> 0);
  const count = positions.length / 3;

  // Pre-warp conservation references.
  let meanBefore = 0;
  let maxHBefore = 0;
  for (let i = 0; i < positions.length; i += 3) {
    meanBefore += Math.hypot(positions[i]!, positions[i + 1]!, positions[i + 2]!);
    maxHBefore = Math.max(maxHBefore, Math.hypot(positions[i]!, positions[i + 2]!));
  }
  meanBefore /= count;

  // 1) Volume-neutral ellipsoid scale. Cliffs keep y EXACTLY fixed (their jitter
  // already stretches them tall, the visual top must stay inside the pre-#348
  // collision envelope, and shrinking y collapses their AABB mass) — their ovalness
  // lives entirely in the horizontal plane (area-neutral: sx·sz = 1).
  let sx = 0.90 + rng() * 0.20;
  let sy = cliff ? 1 : 0.90 + rng() * 0.20;
  let sz = 0.90 + rng() * 0.20;
  const norm = cliff ? Math.sqrt(sx * sz) : Math.cbrt(sx * sy * sz);
  sx /= norm; sz /= norm;
  if (!cliff) sy /= norm;

  // 2) Mild shear: x/z drift with height (strata lean).
  const shearX = (rng() - 0.5) * (cliff ? 0.22 : 0.13);
  const shearZ = (rng() - 0.5) * (cliff ? 0.22 : 0.13);

  // 3) Outward lobes.
  const lobeCount = 2 + (rng() < 0.5 ? 1 : 0);
  const lobes: Array<{ x: number; y: number; z: number; amp: number }> = [];
  for (let l = 0; l < lobeCount; l++) {
    const az = rng() * Math.PI * 2;
    // Cliffs: near-horizontal lobe directions (shoulders/buttresses); boulders roam.
    const el = cliff ? (rng() - 0.4) * 0.5 : (rng() - 0.35) * 1.2;
    // Cliffs push a little harder: the mean-radius renormalisation below compresses
    // their jitter spread (outward-only lobes always raise the mean), so the lobes
    // must add back at least the relief the conservation eats.
    lobes.push({
      x: Math.cos(el) * Math.cos(az),
      y: Math.sin(el),
      z: Math.cos(el) * Math.sin(az),
      amp: size * (cliff ? 0.09 + rng() * 0.09 : 0.07 + rng() * 0.07),
    });
  }

  for (let i = 0; i < positions.length; i += 3) {
    let x = positions[i]! * sx;
    let y = positions[i + 1]! * sy;
    let z = positions[i + 2]! * sz;
    x += y * shearX;
    z += y * shearZ;
    const r = Math.hypot(x, y, z);
    if (r > 0) {
      for (const lobe of lobes) {
        const d = (x * lobe.x + y * lobe.y + z * lobe.z) / r;
        if (d > 0) {
          const push = lobe.amp * d * d;
          x += lobe.x * push;
          y += lobe.y * push;
          z += lobe.z * push;
        }
      }
    }
    positions[i] = x;
    positions[i + 1] = y;
    positions[i + 2] = z;
  }

  // Mass conservation: renormalise so the mean radius is EXACTLY the pre-warp mean.
  let meanAfter = 0;
  for (let i = 0; i < positions.length; i += 3) {
    meanAfter += Math.hypot(positions[i]!, positions[i + 1]!, positions[i + 2]!);
  }
  meanAfter /= count;
  const renorm = meanAfter > 0 ? meanBefore / meanAfter : 1;

  // Clamps: horizontal-growth guard (#348 freeze) and the grounding envelopes.
  // The bound is ABSOLUTE vs the pre-warp footprint — deliberately NOT scaled by
  // `renorm`: when the ellipsoid/shear shrink the mean and renorm > 1, a
  // renorm-scaled bound would let the footprint outgrow +5% (a size-2.2 pinch
  // crag reached 3.014u that way, poking past its 3u gate-to-corridor margin —
  // Codex review on #389). Trimming a couple of rim vertices instead is fine.
  const maxH = maxHBefore * WARP_MAX_HORIZONTAL_GROWTH;
  const maxR = size * (cliff ? WARP_MAX_RADIAL_CLIFF : WARP_MAX_RADIAL_BOULDER);
  for (let i = 0; i < positions.length; i += 3) {
    let x = positions[i]! * renorm;
    let y = positions[i + 1]! * renorm;
    let z = positions[i + 2]! * renorm;
    const r = Math.hypot(x, y, z);
    if (r > maxR) {
      const s = maxR / r;
      x *= s; y *= s; z *= s;
    }
    positions[i] = x;
    positions[i + 1] = y;
    positions[i + 2] = z;
  }
  clampHorizontal(positions, maxH);
  return maxH;
}

/**
 * Pull any vertex whose horizontal radius exceeds `maxH` straight back to it (y
 * untouched). Applied inside massWarpRock AND re-applied by createRock after the
 * scrape pass: a scrape plane pulls vertices along its normal, which can INCREASE
 * hypot(x,z) while reducing 3D radius (Codex review on #389), so the warp's
 * horizontal-growth guarantee must be re-asserted on the final shape.
 */
function clampHorizontal(positions: Float32Array, maxH: number): void {
  for (let i = 0; i < positions.length; i += 3) {
    const h = Math.hypot(positions[i]!, positions[i + 2]!);
    if (h > maxH) {
      positions[i]! *= maxH / h;
      positions[i + 2]! *= maxH / h;
    }
  }
}

/**
 * Flatten `count` random planes into the (already jittered) rock hull. Each pass
 * picks a direction d and an offset r ∈ [0.55, 0.9]·size, then pulls every vertex
 * with dot(v,d) > r back toward the plane. Cliffs bias d toward the horizon so the
 * cuts read as sheer faces (plus an occasional top shelf); boulders stay isotropic.
 * The 0.45·size hull floor keeps the shape convex enough that the existing
 * sink-depth grounding in addRocks still works.
 */
function scrapeRock(
  positions: Float32Array, size: number, rng: () => number,
  count: number, strength: number, cliff: boolean
): void {
  const d = new THREE.Vector3();
  const keepSq = (SCRAPE_KEEP_HULL_RADIUS * size) ** 2;
  for (let s = 0; s < count; s++) {
    const az = rng() * Math.PI * 2;
    // Cliffs: mostly horizontal cut normals (sheer faces) + occasional top shelf.
    const el = cliff
      ? (rng() < 0.25 ? 0.9 + rng() * 0.5 : (rng() - 0.5) * 0.6)
      : Math.acos(2 * rng() - 1) - Math.PI / 2; // uniform over the sphere
    d.set(Math.cos(el) * Math.cos(az), Math.sin(el), Math.cos(el) * Math.sin(az));
    const r = size * (SCRAPE_OFFSET_MIN + rng() * SCRAPE_OFFSET_SPAN);
    for (let i = 0; i < positions.length; i += 3) {
      const p = positions[i]! * d.x + positions[i + 1]! * d.y + positions[i + 2]! * d.z;
      if (p <= r) continue;
      const pull = (p - r) * strength;
      const nx = positions[i]! - d.x * pull;
      const ny = positions[i + 1]! - d.y * pull;
      const nz = positions[i + 2]! - d.z * pull;
      if (nx * nx + ny * ny + nz * nz < keepSq) continue; // keep the core hull
      positions[i] = nx; positions[i + 1] = ny; positions[i + 2] = nz;
    }
  }
}

// Create a rock with variable size
export function createRock(size: number, opts: RockOptions = {}): THREE.Mesh {
  const cliff = opts.cliff === true;
  // Cliffs use detail 0 (sharper, blockier facets) and a stronger, vertically biased
  // deformation so they read as craggy outcrops; ordinary rocks stay rounded boulders.
  const geometry = new THREE.DodecahedronGeometry(size, cliff ? 0 : 1);

  // Deform vertices for a more natural shape
  // (writable Float32Array under the read-only ArrayLike<number> type)
  const positions = geometry.attributes.position!.array as Float32Array;
  for (let i = 0; i < positions.length; i += 3) {
    if (cliff) {
      positions[i]!     *= 1 + (Math.random() - 0.25) * 0.5;
      positions[i + 1]! *= 1 + Math.random() * 0.7;        // stretch upward -> taller crag
      positions[i + 2]! *= 1 + (Math.random() - 0.25) * 0.5;
    } else {
      const noise = Math.random() * 0.2;
      positions[i]!     *= (1 + noise);
      positions[i + 1]! *= (1 + noise);
      positions[i + 2]! *= (1 + noise);
    }
  }

  // One deterministic shape seed drives BOTH the scrape pass and the face-aware
  // snow/shading colour pass below (same fallback counter as before — never
  // Math.random, so createRock stays stream-neutral even seedless).
  const shapeSeed = opts.seed ?? (rockSeedCounter = (rockSeedCounter + 0x9E3779B9) | 0);

  // Mass-preserving silhouette warp (#385 PR 3): ellipsoid/shear/outward lobes over
  // the jittered solid, BEFORE the scrape pass so scrape facets stay flat planes cut
  // into the warped mass. Private seeded stream; mean radius conserved exactly.
  // Remember its horizontal bound: the scrape below can push a vertex's hypot(x,z)
  // back OUT while pulling it toward a plane, so the guard is re-asserted after.
  let warpHorizontalBound = Infinity;
  if (opts.warp !== false) {
    warpHorizontalBound = massWarpRock(positions, size, { cliff, seed: shapeSeed });
  }

  // Seeded scrape pass: flatten a few random planes into the jittered hull so
  // boulders read as faceted rock and cliffs as sheared crag faces instead of
  // inflated dodecahedra. Runs BEFORE computeVertexNormals/applyRockSnowColors,
  // so up-facing scrape planes pick up coherent snow shelves for free.
  if (opts.scrape !== false) {
    const rng = mulberry32(shapeSeed);
    const count = opts.scrapeCount ?? (cliff ? 6 + Math.floor(rng() * 4) : 3 + Math.floor(rng() * 3));
    scrapeRock(positions, size, rng, count, opts.scrapeStrength ?? 1, cliff);
    // Re-assert the warp's ≤5% horizontal-footprint guarantee on the FINAL shape
    // (a scrape pull with d_h opposing the vertex's horizontal sign grows
    // hypot(x,z) while shrinking 3D radius — Codex review on #389).
    if (warpHorizontalBound !== Infinity) clampHorizontal(positions, warpHorizontalBound);
  }
  // Placement reach contract (see RockOptions.maxHorizontalReach): applied last so
  // it caps the final shape whatever combination of passes ran.
  if (opts.maxHorizontalReach !== undefined) {
    clampHorizontal(positions, opts.maxHorizontalReach);
  }
  geometry.computeVertexNormals();

  // Snow gathers on the rock's high up-facing shelves (baked into vertex colours,
  // face-aware — see applyRockSnowColors); the base stone colour rides in the same
  // attribute, so the material colour stays white and is modulated per-vertex. A
  // shared craggy normal map adds surface roughness without extra geometry.
  // (issue #17 Stage 2; restrained-snow rework in #385 PR 2)
  const rockColor = makeRockColor(cliff ? 1 : 0);
  applyRockSnowColors(geometry, rockColor, { cliff, seed: shapeSeed });

  const rock = new THREE.Mesh(geometry, getRockMaterial(cliff));
  rock.castShadow = true;
  rock.receiveShadow = true;
  // Tag every rock so addRocks' re-run de-dup sweep can find it by flag rather than
  // geometry type. The type match (`.type.includes('Dodecahedron')`) only works while
  // rocks are dodecahedra; the flag is geometry-agnostic and survives a future change.
  rock.userData.isRock = true;

  // Compatibility burn: sharing the material removed the per-rock MeshStandardMaterial
  // UUID draw (4 global Math.random values) that main made for every rock. Consume the
  // same 4 here so createRock leaves the global stream exactly where main did — otherwise
  // rock/tree placement (terrain-mesh runs addRocks before addTrees on the same stream)
  // shifts and the byte-identical physics/winnability baselines break. Pinned by the
  // RNG-budget test in rock-material-tests.js.
  for (let i = 0; i < ROCK_MATERIAL_UUID_DRAWS; i++) Math.random();

  return rock;
}

/**
 * World-space collision top for a placed rock mesh (#348): the rotated-local
 * bounding-box top over the CANONICAL sink depth (boulder 0.3·size, cliff
 * 0.28·size) — deliberately NOT the grounding layer's slope-aware deeper sink
 * (PR 4, render-only), so a cosmetic sink change can never move a collision top.
 * A deeper-sunk mesh only puts its visible top at-or-below the collision top,
 * which errs on the conservative side (hits register above the visible rock,
 * never through it). Box3 math draws no THREE UUIDs, so the seeded global
 * stream is untouched.
 */
function hazardTopY(rock: THREE.Mesh, terrainHeight: number, size: number, cliff: boolean): number {
  // Exact rotated-local top: rotate every vertex and take max y. Deliberately
  // position-INDEPENDENT (a Box3.setFromObject world box would bake position.y in,
  // and (top + y) − y is not float-exact — the render-only grounding sink would
  // then perturb hazard tops by ulps and break the grounding-neutrality contract).
  // Also tighter than a rotated-AABB corner expansion. ~144 verts per rock — cheap.
  const posAttr = rock.geometry.attributes.position!;
  const m = new THREE.Matrix4().makeRotationFromEuler(rock.rotation);
  const v = new THREE.Vector3();
  let top = -Infinity;
  for (let i = 0; i < posAttr.count; i++) {
    v.fromBufferAttribute(posAttr, i).applyMatrix4(m);
    if (v.y > top) top = v.y;
  }
  return terrainHeight - size * (cliff ? 0.28 : 0.3) + top;
}

// Add rocks to create a more realistic mountain environment.
//
// `outAllRendered`, when supplied, is filled with EVERY rendered rock (scatter boulders
// + cliff blocks), not just the collision-hazard subset that's returned. Contact shadows
// (mountains/contact-shadows.ts) need the full rendered set so decorative rocks — and
// large rocks filtered out of the hazard list by the ski-lane/spawn safety checks — still
// get a grounding blob (Codex review #243). Optional, so existing callers are unchanged.
export function addRocks(
  scene: THREE.Scene,
  outAllRendered?: RenderedRockPosition[],
  opts?: {
    /** Disable the render-only grounding decorations (collars/chips/deep sinks) —
     *  a TEST seam so suites can prove placement + the hazard list are byte-identical
     *  with the decorations on and off (#385 PR 4). Default on. */
    grounding?: boolean;
    /** Keep separate meshes for geometry/collider equivalence tests. */
    batching?: boolean;
  }
): RockHazardPosition[] {
  const grounding = opts?.grounding !== false;
  // Remove any existing rocks from the scene to prevent duplicates. Rocks are tagged
  // `userData.isRock` in createRock (and the merged grounding meshes
  // `userData.isRockGrounding`), so the sweep is geometry-agnostic — it keeps
  // working with merged spatial batches too. Every attached rock/grounding geometry
  // is owned by this build (materials are pooled), so free it when replacing the scene.
  for (let i = scene.children.length - 1; i >= 0; i--) {
    const child = scene.children[i]!;
    if (child.userData && (child.userData.isRock === true || child.userData.isRockGrounding === true)) {
      scene.remove(child);
      if (child instanceof THREE.Mesh) {
        const geometry: unknown = child.geometry;
        if (geometry instanceof THREE.BufferGeometry) geometry.dispose();
      }
    }
  }

  // Create rock positions with higher density on steeper parts of mountain
  const rockPositions: RockPosition[] = [];

  // Add rocks scattered across the entire mountain
  for(let z = -180; z < 90; z += 10) {
    for(let x = -140; x < 140; x += 10) {
      // Skip positions that would be too far from the actual terrain plane
      if (Math.abs(x) > 150 || Math.abs(z) > 200) continue;

      // Random offset for natural placement
      const xPos = x + (Math.random() * 8 - 4);
      const zPos = z + (Math.random() * 8 - 4);

      // Get terrain information at this position
      const y = getTerrainHeight(xPos, zPos);
      const gradient = getTerrainGradient(xPos, zPos);
      const steepness = Math.sqrt(gradient.x*gradient.x + gradient.z*gradient.z);

      // Higher probability of rocks on steeper slopes, but still some randomness
      if(Math.random() < 0.1 + steepness * 0.5) {
        rockPositions.push({x: xPos, y: y, z: zPos, size: 0.5 + Math.random() * 2.5});
      }
    }
  }

  const collisionRockPositions: RockHazardPosition[] = [];

  // Grounding decoration inputs (#385 PR 4), collected during placement and built
  // at the end: snow collars around every large rendered rock, pebble chips at the
  // foot of big cliff blocks. Render-only — never hazards, never in the out lists.
  const collarEntries: GroundingEntry[] = [];
  const chipEntries: GroundingEntry[] = [];

  // Deterministic per-placement scrape seed: derived from the (already
  // stream-stable) placement coordinates, so a given run re-creates identical
  // rock shapes without consuming any global Math.random() draws.
  const shapeSeed = (x: number, z: number): number =>
    (Math.imul(Math.round(x * 100), 73856093) ^ Math.imul(Math.round(z * 100), 19349663)) >>> 0;

  // Create rock instances
  rockPositions.forEach(pos => {
    // Get the exact terrain height from our height map or calculation
    const terrainHeight = getTerrainHeight(pos.x, pos.z);

    const rock = createRock(pos.size, { seed: shapeSeed(pos.x, pos.z) });

    // Sink the rock into the terrain for anchoring — slightly deeper on steep
    // pitches when grounding is on (#385 PR 4; render-only, RockPosition.y keeps
    // its terrain-height semantics and the hazard list is untouched).
    const gradient = getTerrainGradient(pos.x, pos.z);
    const steepness = Math.sqrt(gradient.x * gradient.x + gradient.z * gradient.z);
    const sink = grounding ? rockSinkDepth(pos.size, steepness, false) : pos.size * 0.3;
    rock.position.set(pos.x, terrainHeight - sink, pos.z);

    // Random rotation for natural look
    rock.rotation.y = Math.random() * Math.PI * 2;
    rock.rotation.z = Math.random() * 0.3;

    // Align rock to terrain slope for better anchoring
    rock.rotation.x = Math.atan(gradient.z) * 0.8;
    rock.rotation.z = -Math.atan(gradient.x) * 0.8;

    if (grounding && pos.size >= COLLAR_MIN_SIZE) {
      collarEntries.push({ x: pos.x, z: pos.z, size: pos.size, seed: shapeSeed(pos.x, pos.z) });
    }

    scene.add(rock);
    outAllRendered?.push({ x: pos.x, y: terrainHeight, z: pos.z, size: pos.size });

    if (rockIsCollisionHazard(pos.x, pos.z, pos.size)) {
      collisionRockPositions.push({
        x: pos.x, y: terrainHeight, z: pos.z, size: pos.size,
        topY: hazardTopY(rock, terrainHeight, pos.size, false),
      });
    }
  });

  // Cliff outcrops: a sparse scatter of larger, craggy rock formations on the
  // steepest flanks, kept well clear of the central ski corridor so they add drama
  // and terrain diversity without walling off the run. Each outcrop is a tight
  // cluster of 2-3 cliff blocks so it reads as a rocky band/buttress rather than one
  // lone boulder. Like every rock these are decorative, but any block big enough and
  // clear of the ski line/spawn pocket also registers as a collision hazard (radius
  // capped at 3u, in sync with snowman collision) — you can't ski through a cliff.
  for (let z = -180; z < 80; z += 30) {
    const lane = activeLaneX(z);
    for (let x = -130; x <= 130; x += 30) {
      // Keep the whole formation off the centerline (the ski corridor follows the line).
      if (Math.abs(x - lane) < 18) continue;
      const cx = x + (Math.random() * 14 - 7);
      const cz = z + (Math.random() * 14 - 7);
      if (Math.abs(cx - activeLaneX(cz)) < 16) continue;
      if (Math.abs(cx) > 145 || Math.abs(cz) > 195) continue;

      // Only on genuinely steep pitches, and not on every one even then.
      const gradient = getTerrainGradient(cx, cz);
      const steepness = Math.sqrt(gradient.x * gradient.x + gradient.z * gradient.z);
      if (steepness < 0.22) continue;
      if (Math.random() > 0.28 + steepness * 0.35) continue;

      const baseSize = 3.0 + Math.random() * 2.0; // 3-5u cliff blocks
      const blocks = 2 + Math.floor(Math.random() * 2); // 2-3 blocks per outcrop
      for (let b = 0; b < blocks; b++) {
        const bx = b === 0 ? cx : cx + (Math.random() * 6 - 3);
        const bz = b === 0 ? cz : cz + (Math.random() * 6 - 3);
        if (Math.abs(bx - activeLaneX(bz)) < 14) continue; // never let a satellite block drift onto the line
        const bSize = b === 0 ? baseSize : baseSize * (0.55 + Math.random() * 0.4);
        const bHeight = getTerrainHeight(bx, bz);

        const rock = createRock(bSize, { cliff: true, seed: shapeSeed(bx, bz) });
        rock.position.set(bx, bHeight - bSize * 0.28, bz);
        rock.rotation.y = Math.random() * Math.PI * 2;
        const g = getTerrainGradient(bx, bz);
        rock.rotation.x = Math.atan(g.z) * 0.8;
        rock.rotation.z = -Math.atan(g.x) * 0.8;
        scene.add(rock);
        outAllRendered?.push({ x: bx, y: bHeight, z: bz, size: bSize });

        if (grounding && bSize >= COLLAR_MIN_SIZE) {
          collarEntries.push({ x: bx, z: bz, size: bSize, seed: shapeSeed(bx, bz) });
        }
        if (grounding && bSize >= CHIP_MIN_CLIFF_SIZE) {
          chipEntries.push({ x: bx, z: bz, size: bSize, seed: shapeSeed(bx, bz) });
        }

        if (rockIsCollisionHazard(bx, bz, bSize)) {
          collisionRockPositions.push({
            x: bx, y: bHeight, z: bz, size: bSize,
            topY: hazardTopY(rock, bHeight, bSize, true),
          });
        }
      }
    }
  }

  // Rock-gate pinches (Black). A few collidable rocks framing the corridor at intervals,
  // so the winding line threads a series of rock "gates": drift wide off the line and you
  // clip one. Placed DETERMINISTICALLY at the channel edge (just outside the collidable
  // clear corridor) so the on-line path stays clear and winnable — they punish leaving the
  // line, not following it. Only runs when a corridor line is active; straight tiers add
  // nothing here, so Bunny/Blue consume no extra Math.random() and stay byte-identical.
  // (Provisional placement; tightened/validated against the winnability harness in D3.2d.)
  const courseLine = getActiveCourseLine();
  if (courseLine) {
    const PINCH_Z = [-42, -78, -126, -168];   // between/around the checkpoints (-60,-105,-150)
    const PINCH_EDGE = 8;                       // > ROCK_COLLISION_PATH_HALF_WIDTH + radius ⇒ line stays clear
    const PINCH_SIZE = 2.2;                     // collidable (>= ROCK_COLLISION_MIN_SIZE)
    for (const pz of PINCH_Z) {
      const lx = courseLine.laneX(pz);
      for (const sideSign of [-1, 1]) {
        const rx = lx + sideSign * PINCH_EDGE;
        const ry = getTerrainHeight(rx, pz);
        // The gate's guaranteed clear margin: crag centres sit PINCH_EDGE off the
        // lane and the corridor is ROCK_COLLISION_PATH_HALF_WIDTH wide, so the
        // rendered rock may never reach farther than their difference (3u).
        const rock = createRock(PINCH_SIZE, {
          cliff: true,
          seed: shapeSeed(rx, pz),
          maxHorizontalReach: PINCH_EDGE - ROCK_COLLISION_PATH_HALF_WIDTH,
        });
        rock.position.set(rx, ry - PINCH_SIZE * 0.28, pz);
        // Yaw only — NO slope-alignment pitch/roll for gate crags: a tilt projects
        // tall local y into world x/z, which would defeat the maxHorizontalReach
        // corridor contract enforced on the local buffer above (Codex on #389).
        // Yaw preserves horizontal reach exactly, so local cap ⇒ world cap. The
        // rotation.y draw MUST stay (global stream position); the gradient tilt
        // consumed no draws, so dropping it is stream-neutral. Upright crags read
        // fine — these are deliberate gate monuments, seated by their sink depth.
        rock.rotation.y = Math.random() * Math.PI * 2;
        scene.add(rock);
        if (grounding) {
          collarEntries.push({ x: rx, z: pz, size: PINCH_SIZE, seed: shapeSeed(rx, pz) });
        }
        collisionRockPositions.push({
          x: rx, y: ry, z: pz, size: PINCH_SIZE,
          topY: hazardTopY(rock, ry, PINCH_SIZE, true),
        });
        // Register with the rendered-rock list too (like the scatter + cliff rocks above), so
        // these collidable pinch hazards get the grounding contact-AO blob addContactShadows draws.
        outAllRendered?.push({ x: rx, y: ry, z: pz, size: PINCH_SIZE });
      }
    }
  }

  // Collision tops and full rendered positions were recorded from the originals.
  // This cosmetic merge must happen only after every placement/RNG consumer.
  if (opts?.batching !== false) batchStaticRocks(scene);

  // Build the merged grounding meshes LAST — after every placement loop has
  // finished with the global stream (this consumes zero global draws; UUID draws
  // are guarded inside).
  if (grounding) {
    addRockGrounding(scene, collarEntries, chipEntries);
  }

  console.log(`Mountains.addRocks: Created ${collisionRockPositions.length} rock positions for collision detection`);
  return collisionRockPositions;
}
